import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button, Icon, Skeleton } from "@nexus/ui";
import {
  adminOpsSettingsQueryOptions,
  scheduledJobsQueryOptions,
  useRunNowScheduledJob,
} from "@/lib/queries";
import type { ScheduledJob } from "@/features/scheduled-jobs/api/scheduled-jobs.api";
import { scheduledJobErrorMessage } from "@/features/scheduled-jobs/lib/scheduled-job-errors";
import { useIsSuperTenant, usePermissions } from "@/features/auth/hooks/usePermissions";
import { ADMIN_OPS_FALLBACK } from "@/features/admin/lib/admin-ops-settings";
import { eventTimeRangeHours } from "@/features/events/constants/event-time-ranges";
import {
  filterScheduledJobs,
  listScheduledJobEventTypes,
  scheduledJobsHaveActiveFilters,
  type ScheduledJobsViewFilters,
} from "../lib/scheduled-job-filters";
import { buildScheduledJobKpiStats } from "../lib/scheduled-job-kpis";
import { buildScheduledJobRunSeries } from "../lib/scheduled-job-run-series";
import { ScheduledJobCreateSheet } from "./ScheduledJobCreateSheet";
import { ScheduledJobDeleteDialog } from "./ScheduledJobDeleteDialog";
import { ScheduledJobDetailDrawer } from "./ScheduledJobDetailDrawer";
import { ScheduledJobsActivityChart } from "./ScheduledJobsActivityChart";
import { ScheduledJobsEmptyState } from "./ScheduledJobsEmptyState";
import { ScheduledJobsFilterBar } from "./ScheduledJobsFilterBar";
import { ScheduledJobsKpiStrip } from "./ScheduledJobsKpiStrip";
import { ScheduledJobsTable } from "./ScheduledJobsTable";

/** Cadences run in hours, not seconds, so "now" only needs to be fresh enough to catch a job crossing into "overdue". */
const NOW_REFRESH_INTERVAL_MS = 60 * 1000;

interface ScheduledJobsPageProps {
  filters: ScheduledJobsViewFilters;
  onFiltersChange: (next: ScheduledJobsViewFilters) => void;
}

export function ScheduledJobsPage({ filters, onFiltersChange }: ScheduledJobsPageProps) {
  const { t } = useTranslation();
  const isSuperTenant = useIsSuperTenant();
  const perms = usePermissions();
  const [now, setNow] = useState(() => Date.now());
  const [selectedJob, setSelectedJob] = useState<ScheduledJob | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [deleteJobId, setDeleteJobId] = useState<string | null>(null);
  const windowHours = eventTimeRangeHours(filters.range);
  const runNow = useRunNowScheduledJob();

  // How this tab reads health and whether it refreshes itself. A viewer
  // without the grant gets the same defaults a fresh install shows.
  const { data: opsSettings } = useQuery({
    ...adminOpsSettingsQueryOptions(),
    enabled: isSuperTenant || perms.ADMIN_GET_OPS_SETTINGS.isOn,
  });
  const ops = opsSettings ?? ADMIN_OPS_FALLBACK;
  const overdueGraceMs = ops.jobsOverdueGraceMinutes * 60 * 1000;
  const unhealthyAfterFailures = ops.jobsUnhealthyAfterFailures;

  const { data, isLoading, isError, isFetching, isPlaceholderData, refetch } = useQuery({
    ...scheduledJobsQueryOptions(windowHours),
    enabled: isSuperTenant,
    refetchInterval: ops.jobsAutoRefreshSeconds > 0 ? ops.jobsAutoRefreshSeconds * 1000 : false,
  });
  const rows = data ?? [];
  // First paint only — window flips keep the previous rows via keepPreviousData.
  const showInitialSkeleton = isLoading && !isPlaceholderData && rows.length === 0;

  const filteredRows = useMemo(
    () =>
      filterScheduledJobs(rows, filters, {
        now,
        overdueGraceMs,
        unhealthyAfterFailures,
      }),
    [filters, now, overdueGraceMs, rows, unhealthyAfterFailures],
  );

  const eventTypeOptions = useMemo(() => listScheduledJobEventTypes(rows), [rows]);
  const hasActiveFilters = scheduledJobsHaveActiveFilters(filters);
  const kpiStats = useMemo(
    () =>
      buildScheduledJobKpiStats(filteredRows, {
        now,
        overdueGraceMs,
        unhealthyAfterFailures,
      }),
    [filteredRows, now, overdueGraceMs, unhealthyAfterFailures],
  );
  const runSeries = useMemo(
    () => buildScheduledJobRunSeries(filteredRows),
    [filteredRows],
  );

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), NOW_REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  // Keep the open drawer on the latest row for this job after a window refetch.
  useEffect(() => {
    if (!selectedJob) return;
    const match = rows.find((row) => row.name === selectedJob.name);
    if (match && match !== selectedJob) setSelectedJob(match);
  }, [rows, selectedJob]);

  const handleRunNow = (jobId: string) => {
    runNow.mutate(jobId, {
      onSuccess: (result) => {
        toast.success(t("admin.scheduledJobs.runNow.sent", "Job triggered"), {
          description: result.correlationId,
        });
      },
      onError: (error) => {
        toast.error(
          scheduledJobErrorMessage(
            error,
            t(
              "admin.scheduledJobs.mutationFailed",
              "Could not update this job. Check Cloud Scheduler write IAM if the action was pause, resume, save, or delete.",
            ),
          ),
        );
      },
    });
  };

  const handleClearFilters = () => {
    onFiltersChange({ range: filters.range });
  };

  const handleToggleHealthFilter = (health: "failing" | "overdue") => {
    onFiltersChange({
      ...filters,
      health: filters.health === health ? undefined : health,
    });
  };

  if (!isSuperTenant) {
    return (
      <p className="m-0 max-w-[68ch] text-xs text-muted-foreground">
        {t(
          "admin.scheduledJobs.noAccess",
          "Scheduled jobs are platform infrastructure, visible to platform admins only.",
        )}
      </p>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          {t(
            "admin.scheduledJobs.subtitle",
            "Live jobs reported by Cloud Scheduler, enriched with recent run history.",
          )}
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            type="button"
            size="sm"
            className="h-8 rounded-sm text-xs"
            iconStart="plus"
            onClick={() => setIsCreateOpen(true)}
          >
            {t("admin.scheduledJobs.createAction", "Create job")}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 shrink-0 rounded-sm text-xs"
            onClick={() => void refetch()}
          >
            <Icon
              name="refresh-cw"
              size="xs"
              color="inherit"
              aria-hidden
              className={isFetching ? "animate-spin motion-reduce:animate-none" : undefined}
            />
            {t("admin.scheduledJobs.refresh", "Refresh")}
          </Button>
        </div>
      </div>

      {!isError && (showInitialSkeleton || rows.length > 0) ? (
        <ScheduledJobsFilterBar
          filters={filters}
          eventTypeOptions={eventTypeOptions}
          resultCount={showInitialSkeleton ? null : filteredRows.length}
          hasActiveFilters={hasActiveFilters}
          onFiltersChange={onFiltersChange}
          onClearFilters={handleClearFilters}
        />
      ) : null}

      {!isError && (showInitialSkeleton || rows.length > 0) ? (
        <ScheduledJobsKpiStrip
          stats={showInitialSkeleton ? undefined : kpiStats}
          avgDurationMs={runSeries.avgDurationMs}
          p95DurationMs={runSeries.p95DurationMs}
          isLoading={showInitialSkeleton}
          isStale={isPlaceholderData}
          isFailingFilterActive={filters.health === "failing"}
          isOverdueFilterActive={filters.health === "overdue"}
          onFilterFailing={() => handleToggleHealthFilter("failing")}
          onFilterOverdue={() => handleToggleHealthFilter("overdue")}
        />
      ) : null}

      {!isError && (showInitialSkeleton || rows.length > 0) ? (
        <ScheduledJobsActivityChart
          jobs={filteredRows}
          isLoading={showInitialSkeleton}
          isPlaceholderData={isPlaceholderData}
          windowHours={windowHours}
          selectedJobName={selectedJob?.name ?? null}
          onSelectJob={(jobName) => {
            const match = rows.find((row) => row.name === jobName);
            if (match) setSelectedJob(match);
          }}
          onFilterFailed={() => handleToggleHealthFilter("failing")}
          isFailedFilterActive={filters.health === "failing"}
        />
      ) : null}

      <div className="min-h-0 flex-1 overflow-auto rounded-md border border-border bg-card">
        {showInitialSkeleton ? (
          <div className="space-y-2 p-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : isError ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-8">
            <p className="text-sm text-muted-foreground">
              {t("admin.scheduledJobs.loadError", "Failed to load scheduled jobs.")}
            </p>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              {t("admin.scheduledJobs.retry", "Retry")}
            </Button>
          </div>
        ) : rows.length === 0 ? (
          <ScheduledJobsEmptyState />
        ) : filteredRows.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-8">
            <p className="text-sm text-muted-foreground">
              {t(
                "admin.scheduledJobs.filters.empty",
                "No jobs match these filters.",
              )}
            </p>
            <Button variant="outline" size="sm" onClick={handleClearFilters}>
              {t("admin.scheduledJobs.filters.clear", "Clear filters")}
            </Button>
          </div>
        ) : (
          <ScheduledJobsTable
            rows={filteredRows}
            now={now}
            overdueGraceMs={overdueGraceMs}
            unhealthyAfterFailures={unhealthyAfterFailures}
            onSelectRow={setSelectedJob}
            selectedRowId={selectedJob?.name ?? null}
          />
        )}
      </div>

      <ScheduledJobDetailDrawer
        job={selectedJob}
        now={now}
        overdueGraceMs={overdueGraceMs}
        unhealthyAfterFailures={unhealthyAfterFailures}
        onClose={() => setSelectedJob(null)}
        onRunNow={handleRunNow}
        onRequestDelete={(jobId) => setDeleteJobId(jobId)}
        isRunNowPending={runNow.isPending}
      />

      <ScheduledJobCreateSheet
        isOpen={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        onCreated={(jobId) => {
          toast.success(t("admin.scheduledJobs.create.created", "Job created"), {
            description: jobId,
          });
        }}
      />

      <ScheduledJobDeleteDialog
        jobId={deleteJobId}
        onOpenChange={(open) => {
          if (!open) setDeleteJobId(null);
        }}
        onDeleted={(jobId) => {
          if (selectedJob?.id === jobId) setSelectedJob(null);
          toast.success(t("admin.scheduledJobs.delete.deleted", "Job deleted"), {
            description: jobId,
          });
        }}
      />
    </div>
  );
}
